<script setup lang="ts">
import { copyTextToClipboard, DialogModal, InstagramIcon, SnapchatIcon, TiktokIcon, WebsiteIcon, YoutubeIcon } from '@metapic-apps/shared'
import type { FormInstance, FormRules } from 'element-plus'
import { computed, reactive, ref, watch } from 'vue'

import { useConvertAffiliateLink, useInspectAffiliateLink } from '@/api/affiliate-links.api'
import LinkIcon from '@/assets/icons/link.svg?component'
import { urlRule } from '@/components/Advertiser/UrlMatchRuleForm/validators'
import CostDataRow from '@/components/CampaignListItem/CostDataRow.vue'
import AdvertiserPicker from '@/components/ReturnAdForm/AdvertiserPicker/AdvertiserPicker.vue'
import { notification } from '@/lib/helpers'
import type { AdvertiserType } from '@/lib/types/advertiser.types'
import { BoxComponent, ButtonComponent, FlexComponent, InputComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  userId: number
}>()

const emit = defineEmits<{
  converted: []
}>()

const isOpen = defineModel<boolean>({ default: false })

const formRef = ref<FormInstance>()
const formData = reactive({ url: '' })
const rules: FormRules = {
  url: [{ required: true, message: 'URL is required', trigger: 'blur' }, urlRule],
}

const possibleAdvertisers = ref<AdvertiserType[]>([])
const selectedAdvertiser = ref<AdvertiserType | undefined>()
const isAdvertiserPickerOpen = ref(false)
const isCopied = ref(false)

const { inspect, isLoading: isInspecting } = useInspectAffiliateLink(props.userId)
const { convert, convertedLink, normalizedCosts, clearConvertedLink, isLoading: isConverting } = useConvertAffiliateLink(props.userId)

const isLoading = computed(() => isInspecting.value || isConverting.value)

const close = () => {
  isOpen.value = false
  formData.url = ''
  formRef.value?.clearValidate()
  possibleAdvertisers.value = []
  selectedAdvertiser.value = undefined
  clearConvertedLink()
  isCopied.value = false
}

const clearLink = () => {
  clearConvertedLink()
  formData.url = ''
  isCopied.value = false
}

const onCopy = () => {
  if (!convertedLink.value?.mtpc_url) {
    return
  }
  copyTextToClipboard(convertedLink.value.mtpc_url).catch(() => undefined)
  isCopied.value = true
  setTimeout(() => {
    isCopied.value = false
  }, 2000)
}

const doConvert = async (advertiserId?: number) => {
  const result = await convert(formData.url, advertiserId)
  if (!result) {
    return
  }
  possibleAdvertisers.value = []
  selectedAdvertiser.value = undefined
  emit('converted')
}

const onConvert = async () => {
  const valid = await formRef.value?.validate().catch(() => false)
  if (!valid) {
    return
  }
  const result = await inspect(formData.url)
  if (result === null) {
    return
  }

  switch (result.inspectionResult) {
    case 'single_advertiser':
    case 'affiliate_link':
      await doConvert(result.advertisers[0]?.id)
      break
    case 'multiple_advertisers':
      possibleAdvertisers.value = result.advertisers
      isAdvertiserPickerOpen.value = true
      break
    case 'no_matching_advertisers':
      notification.error("This link doesn't match any advertiser")
      break
  }
}

watch(isAdvertiserPickerOpen, (open) => {
  if (!open && selectedAdvertiser.value) {
    void doConvert(selectedAdvertiser.value.id)
  }
})
</script>

<template>
  <DialogModal v-model="isOpen" width="560" @before-close="close">
    <template #header>
      <TypographyComponent variant="headline-medium">
        {{ convertedLink ? 'Metapic Link Created! 🎉' : 'Convert link' }}
      </TypographyComponent>
    </template>

    <template #default>
      <template v-if="convertedLink">
        <BoxComponent :mt="24">
          <InputComponent :model-value="convertedLink.mtpc_url" :is-disabled="true" />
        </BoxComponent>

        <BoxComponent v-if="convertedLink.advertiser?.revenue_text" :mt="24">
          <FlexComponent direction="column" align="center" :gap="4">
            <FlexComponent :gap="4" align="center">
              <ElIcon :size="16"><InstagramIcon /></ElIcon>
              <ElIcon :size="16"><TiktokIcon /></ElIcon>
              <ElIcon :size="16"><SnapchatIcon /></ElIcon>
              <ElIcon :size="16"><YoutubeIcon /></ElIcon>
              <ElIcon :size="16"><WebsiteIcon /></ElIcon>
            </FlexComponent>
            <TypographyComponent variant="label-large">
              {{ convertedLink.advertiser.revenue_text }}
            </TypographyComponent>
          </FlexComponent>
        </BoxComponent>

        <BoxComponent v-else-if="normalizedCosts.length" :mt="24">
          <CostDataRow :currency="convertedLink.advertiser?.currency ?? ''" :grouped-traffic-source-costs="normalizedCosts" />
        </BoxComponent>

        <BoxComponent :mt="32">
          <FlexComponent justify="flex-end" :gap="8">
            <ButtonComponent variant="text" @click="clearLink">Clear</ButtonComponent>
            <ButtonComponent variant="primary" @click="onCopy">
              {{ isCopied ? 'Copied!' : 'Copy' }}
            </ButtonComponent>
          </FlexComponent>
        </BoxComponent>
      </template>

      <template v-else>
        <ElForm ref="formRef" :model="formData" :rules="rules" hide-required-asterisk :scroll-to-error="true">
          <BoxComponent :mt="24">
            <ElFormItem prop="url">
              <InputComponent v-model="formData.url" name="url" placeholder="Paste link here">
                <template #prefix>
                  <ElIcon>
                    <LinkIcon />
                  </ElIcon>
                </template>
              </InputComponent>
            </ElFormItem>
          </BoxComponent>
        </ElForm>
        <BoxComponent :mt="32">
          <FlexComponent justify="flex-end" :gap="8">
            <ButtonComponent variant="text" :is-disabled="isLoading" @click="close">Cancel</ButtonComponent>
            <ButtonComponent variant="primary" :is-loading="isLoading" :is-disabled="!formData.url" @click="onConvert"
              >Convert</ButtonComponent
            >
          </FlexComponent>
        </BoxComponent>
      </template>
    </template>
  </DialogModal>

  <AdvertiserPicker
    v-if="possibleAdvertisers.length > 0"
    v-model="isAdvertiserPickerOpen"
    v-model:selected-advertiser="selectedAdvertiser"
    :advertisers="possibleAdvertisers"
  />
</template>
